import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { setAuthToken } from '../utils/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = useCallback(async (userId) => {
    console.log("Fetching profile for:", userId);
    
    // Create a timeout promise
    const timeout = new Promise((_, reject) => 
      setTimeout(() => reject(new Error("Profile fetch timeout")), 5000)
    );

    try {
      const query = supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      const { data, error } = await Promise.race([query, timeout]);
      
      if (error) {
        console.error('Error fetching profile:', error);
        return null;
      }
      console.log("Profile fetched successfully:", !!data);
      return data;
    } catch (err) {
      console.warn("Profile fetch failed or timed out:", err.message);
      return null;
    }
  }, []);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        // 1. Get initial session
        const { data: { session: initSession }, error } = await supabase.auth.getSession();
        if (error) throw error;

        if (mounted) {
          if (initSession) {
            console.log("Session found on refresh:", initSession.user.email);
            setSession(initSession);
            setAuthToken(initSession.access_token);
            const profile = await fetchProfile(initSession.user.id);
            if (mounted) setUser({ ...initSession.user, ...profile });
          } else {
            console.log("No initial session found on refresh.");
          }
        }
      } catch (err) {
        console.error("Auth initialization error:", err);
      } finally {
        if (mounted) setLoading(false);
      }
    };

    initializeAuth();

    // 2. Listen for future changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, currentSession) => {
      if (!mounted) return;
      console.log("Auth Change Event:", event, !!currentSession);

      if (currentSession) {
        setSession(currentSession);
        setAuthToken(currentSession.access_token);
        
        // Only fetch profile if user state is missing or event is SIGNED_IN/TOKEN_REFRESHED
        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED' || !user) {
          const profile = await fetchProfile(currentSession.user.id);
          if (mounted) {
            setUser({ ...currentSession.user, ...profile });
            setLoading(false);
          }
        }
      } else {
        setSession(null);
        setUser(null);
        setAuthToken(null);
        setLoading(false);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const getHeaders = useCallback(() => {
    return session ? { Authorization: `Bearer ${session.access_token}` } : {};
  }, [session]);

  const login = async (email, password) => {
    console.log("Login attempt started for:", email);
    try {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) {
        console.error("Login error from Supabase:", error);
        throw error;
      }
      
      console.log("Login success, session created:", !!data.session);
      if (data.session) {
        setSession(data.session);
        setAuthToken(data.session.access_token);
        
        // We return the basic user immediately so the UI can proceed
        // The listener will fill in the profile details (role, etc.) shortly
        const basicUser = data.user;
        setUser(basicUser); 
        console.log("Basic user set, modal should close now.");
        return basicUser;
      }
      
      return null;
    } catch (err) {
      console.error("Unexpected error in login function:", err);
      throw err;
    }
  };

  const loginWithGoogle = useCallback(async () => {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.origin
      }
    });
    if (error) throw error;
    return data;
  }, []);

  const register = useCallback(async (name, email, phone, password) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name, phone: phone }
      }
    });
    if (error) throw error;
    
    if (data.user) {
      // Manually create profile because triggers might not be set up
      const { error: pErr } = await supabase
        .from('profiles')
        .insert({
          id: data.user.id,
          name: name,
          email: email.toLowerCase(),
          phone: phone,
          role: 'customer'
        });
      
      if (pErr) console.error("Manual profile creation failed:", pErr);
      
      // If we have a session immediately (autologin), update user state
      if (data.session) {
        setSession(data.session);
        setAuthToken(data.session.access_token);
        setUser({ ...data.user, name, email, phone, role: 'customer' });
      }
    }
    
    return data.user;
  }, []);

  const logout = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
    setUser(null);
    setAuthToken(null);
  }, []);

  const refreshUser = useCallback(async () => {
    if (session) {
      const profile = await fetchProfile(session.user.id);
      setUser({ ...session.user, ...profile });
    }
  }, [session, fetchProfile]);

  const resetPassword = async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    if (error) throw error;
  };

  const updatePassword = async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session,
      loading, 
      login, 
      loginWithGoogle,
      register, 
      logout, 
      getHeaders, 
      refreshUser,
      resetPassword,
      updatePassword
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

const API = process.env.REACT_APP_API_URL || 'https://thebarbercraft-f91w.onrender.com/api';
export { API };
